import { LocationMetricRow } from './types'
import { roundDownToTenth, roundUpToTenth } from './utils'

interface MetricsData {
  locations?: Array<{
    locationId: number
    metricData?: Record<string, any> | null
  }>
  summary?: Record<string, any>
}

interface LocationDetail {
  code: string
  name: string
}

// Process location data into table rows
export const processLocationData = (
  location: any,
  locationsMap: Record<number, LocationDetail>,
  datasetId: number = 1,
  startDate?: string,
  laborVariance: LocationMetricRow['laborVariance'] = null,
): LocationMetricRow => {
  const locationDetail = locationsMap[location.locationId] || {
    code: '',
    name: '',
  }

  // Determine if we should use new metrics (after 2026-04-04)
  const useNewMetrics = startDate ? startDate >= '2026-04-04' : false

  return {
    locationId: location.locationId,
    locationCode: locationDetail?.code || '',
    locationName: locationDetail?.name || '',
    datasetId,
    bonusAttainment:
      location.metricData?.bonus_attainment_percent_quarter || null,
    netSales: location.metricData?.net_sales || null,
    avgWeeklySales: location.metricData?.average_weekly_sales_quarter || null,
    bonusSalesToNextBand: location.metricData?.bonus_sales_to_next_band || null,
    foodVariance: useNewMetrics
      ? location.metricData?.large_meat_variance || null
      : location.metricData?.food_and_beverage_variance || null,
    paperVariance: location.metricData?.paper_variance || null,
    // why: labor_variance comes from a separate query clamped to the last
    // posted labor week, not from this row's metricData (ENG-5468); null when
    // no posted week falls in range, rendering Labor Variance blank
    laborVariance: laborVariance || null,
    totalVariance: location.metricData?.total_prime_cost_variance || null,
    osat: location.metricData?.qualtrics_osat_percent || null,
    google: location.metricData?.google_rating || null,
    delivery:
      location.metricData?.delivery_avg_customer_review_score_doordash || null,
    // Special handling for store 1682 (Location ID: 181) - use qualtrics_speed_of_service_percent instead of hme_goal_percent
    sosAttainment:
      locationDetail?.code === '1682' || location.locationId === 181
        ? location.metricData?.qualtrics_speed_of_service_percent || null
        : location.metricData?.hme_goal_percent || null,
    sosQualtricsAttainment:
      locationDetail?.code === '1682' || location.locationId === 181
        ? location.metricData?.qualtrics_speed_of_service_percent || null
        : location.metricData?.qualtrics_drive_thru_wait_time_percent || null,
    opPrimeCostBonus: location.metricData?.bonus_op_prime_cost_quarter || null,
    opGuestBonus: location.metricData?.bonus_op_guest_metric_quarter || null,
    opTotalBonus: location.metricData?.bonus_op_total_quarter || null,
    opMissedOpportunity:
      location.metricData?.bonus_op_missed_opportunity_quarter || null,
    managerPrimeCostBonus:
      location.metricData?.bonus_manager_prime_cost_quarter || null,
    managerGuestBonus:
      location.metricData?.bonus_manager_guest_metric_quarter || null,
    managerTotalBonus: location.metricData?.bonus_manager_total_quarter || null,
    managerMissedOpportunity:
      location.metricData?.bonus_manager_missed_opportunity_quarter || null,
  }
}

// Check if a row has any non-null metric values
export const hasValidMetricData = (row: LocationMetricRow): boolean => {
  return (
    row.bonusAttainment !== null ||
    row.netSales !== null ||
    row.avgWeeklySales !== null ||
    row.bonusSalesToNextBand !== null ||
    row.foodVariance !== null ||
    row.paperVariance !== null ||
    row.laborVariance !== null ||
    row.totalVariance !== null ||
    row.osat !== null ||
    row.google !== null ||
    row.delivery !== null ||
    row.sosAttainment !== null ||
    row.opPrimeCostBonus !== null ||
    row.opGuestBonus !== null ||
    row.opTotalBonus !== null ||
    row.managerPrimeCostBonus !== null ||
    row.managerGuestBonus !== null ||
    row.managerTotalBonus !== null
  )
}

// Create summary row from metrics data
export const createSummaryRow = (
  summaryData: any,
  locationId: number,
  locationName: string,
  datasetId: number = 1,
  startDate?: string,
  laborVariance: LocationMetricRow['laborVariance'] = null,
): LocationMetricRow => {
  // Determine if we should use new metrics (after 2026-04-04)
  const useNewMetrics = startDate ? startDate >= '2026-04-04' : false

  return {
    locationId,
    locationCode: '',
    locationName,
    datasetId,
    bonusAttainment: summaryData.bonus_attainment_percent_quarter || null,
    netSales: summaryData.net_sales || null,
    avgWeeklySales: summaryData.average_weekly_sales_quarter || null,
    bonusSalesToNextBand: summaryData.bonus_sales_to_next_band || null,
    foodVariance: useNewMetrics
      ? summaryData.large_meat_variance || null
      : summaryData.food_and_beverage_variance || null,
    paperVariance: summaryData.paper_variance || null,
    // sync: keep in step with processLocationData above (ENG-5468 merge)
    laborVariance: laborVariance || null,
    totalVariance: summaryData.total_prime_cost_variance || null,
    osat: summaryData.qualtrics_osat_percent || null,
    google: summaryData.google_rating || null,
    delivery: summaryData.delivery_avg_customer_review_score_doordash || null,
    sosAttainment: summaryData.hme_goal_percent || null,
    sosQualtricsAttainment:
      summaryData.qualtrics_drive_thru_wait_time_percent || null,
    opPrimeCostBonus: null,
    opGuestBonus: null,
    opTotalBonus: null,
    opMissedOpportunity: null,
    managerPrimeCostBonus: null,
    managerGuestBonus: null,
    managerTotalBonus: null,
    managerMissedOpportunity: null,
  }
}

// Process all metrics data into table rows
export const processMetricsData = (
  metricsData: MetricsData | undefined | null,
  locationsMap: Record<number, LocationDetail> | undefined | null,
  marketLeaderName: string = 'TOTAL',
  datasetId: number = 1,
  startDate?: string,
  laborVarianceData?: MetricsData | null,
): LocationMetricRow[] => {
  if (!metricsData?.locations || !locationsMap) {
    return []
  }

  const result: LocationMetricRow[] = []

  const laborVarianceByLocationId: Record<
    number,
    LocationMetricRow['laborVariance']
  > = {}
  laborVarianceData?.locations?.forEach((location) => {
    laborVarianceByLocationId[location.locationId] =
      location.metricData?.labor_variance || null
  })

  // Process location rows
  const locationRows = metricsData.locations
    .map((location) =>
      processLocationData(
        location,
        locationsMap,
        datasetId,
        startDate,
        laborVarianceByLocationId[location.locationId] || null,
      ),
    )
    .filter(hasValidMetricData)

  result.push(...locationRows)

  // Add summary row if available
  if (metricsData.summary) {
    // Use odd negative numbers for summary rows: -1 for main, -3, -5, -7... for others
    const summaryLocationId = datasetId === 1 ? -1 : -(datasetId * 2 - 1)
    const summaryRow = createSummaryRow(
      metricsData.summary,
      summaryLocationId,
      marketLeaderName,
      datasetId,
      startDate,
      laborVarianceData?.summary?.labor_variance || null,
    )
    result.push(summaryRow)
  }

  return result
}
